import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Alert, Button, Card, Form, InputNumber, notification, Select} from 'antd';

import {getBulkConsignment, updateReceivedBulkProduct} from './actions';
import {getDescriptionFromError} from "../utils/errors";
import {formItemLayout} from "../utils/style";
import {bindActionCreators} from "redux";
import units from "../Product/units";
import {NormalisedUnitFactor} from "./NormalisedQuantityDisplay";
import MoneyDisplay from "../common/MoneyDisplay";
import {roundAmount} from "../utils/money";
import {decodeParams} from "../utils/params";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;
const Option = Select.Option;
const MaxDecimals = 6;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

const PackSuggestion = ({pack, factor}) => {
  const origRound = roundAmount(pack.netCostPerUnit ,MaxDecimals);
  const suggestRound = roundAmount(factor * pack.netCostPerUnit, MaxDecimals);
  return [
    MoneyDisplay.defaultProps.prefix,
    origRound.toString(),
    ' x ',
    roundAmount(factor, MaxDecimals),
    ' = ',
    MoneyDisplay.defaultProps.prefix,
    suggestRound.toString(),
  ];
};
PackSuggestion.propTypes = {
  pack: PropTypes.object.isRequired,
  factor: PropTypes.number.isRequired,
};

const PacksAdvice = ({packs, factor}) => {
  if (typeof factor !== 'number') return '';
  const hasPacks = packs && packs.length;
  let message = "There are no packs so no further changes are needed.";
  if (hasPacks) {
    message = <React.Fragment>
      This update will NOT automatically change &quot;Net Cost Per Unit&quot; on {packs.length} pack[s].
      Note the numbers below and correct the packs manually if desired:
      <ul>
        {packs.map((pack, ix) => <li key={`suggest-${ix}`}><PackSuggestion factor={factor} pack={pack}/></li>)}
      </ul>
    </React.Fragment>;
  }

  return <Alert type={hasPacks ? 'warning' : 'info'} message={message} showIcon/>;
};
PacksAdvice.propTypes = {
  packs: PropTypes.array,
  factor: PropTypes.number,
};

class UpdateReceivedBulkProductForm extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    /* actions */
    updateReceivedBulkProduct: PropTypes.func.isRequired,
    getBulkConsignment: PropTypes.func.isRequired,
    /* state */
    loading: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
    bulkConsignment: PropTypes.object.isRequired,
  };

  componentDidMount() {
    this._loadBulkConsignment();
  }

  componentDidUpdate(prevProps) {
    if (this.props.match.params.bulkConsignmentId !== prevProps.match.params.bulkConsignmentId) this._loadBulkConsignment();
  }

  _loadBulkConsignment() {
    const {bulkConsignmentId} = decodeParams(this.props.match.params);
    this.props.getBulkConsignment({bulkConsignmentId});
  }

  goBack = () => {
    this.props.history.goBack();
  };

  handleSubmit = (e) => {
    const {bulkConsignmentId} = this.props.bulkConsignment;
    e.preventDefault();
    this.props.form.validateFields(async(err, values) => {
      if (err) return;
      try {
        await this.props.updateReceivedBulkProduct({...values, bulkConsignmentId});
        this.goBack();
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        });
      }
    });
  };

  render() {
    const {form: {getFieldDecorator, getFieldsError, getFieldValue}, loading, bulkConsignment} = this.props;
    const initialQuantity = bulkConsignment.quantity / NormalisedUnitFactor[bulkConsignment.receivedUnit];
    const oldUnitCost = bulkConsignment.netCost / bulkConsignment.quantity;
    const unit = getFieldValue('unit');
    const quantity = getFieldValue('quantity');
    const netCost = getFieldValue('netCost');
    let unitCostFactor = null;
    if (unit && quantity && netCost) {
      const normQuantity = quantity * NormalisedUnitFactor[unit];
      const unitCost = netCost / normQuantity;
      unitCostFactor = unitCost / oldUnitCost;
    }
    const notApplicable = unitCostFactor === null || isNaN(unitCostFactor) || unitCostFactor === 1;

    return (
      <Card title="Update Received Bulk Product">
        <Form onSubmit={this.handleSubmit}>
          <FormItem
            {...formItemLayout}
            label="Bulk Product"
          >
            {bulkConsignment.bulkProductName}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Lot Number"
          >
            {bulkConsignment.lotNumber}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Quantity"
          >
            {getFieldDecorator('quantity', {
              initialValue: initialQuantity,
              rules: [{
                required: true,
                message: 'Quantity is required'
              }, {
                type: 'number',
                min: 0.001,
                message: 'Must be greater or equal to 0.001'
              }]
            })(
              <InputNumber precision={3}/>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Unit"
          >
            {getFieldDecorator('unit', {
              initialValue: bulkConsignment.receivedUnit,
              rules: [{
                required: true,
                message: 'Unit is required'
              }]
            })(
              <Select>
                {units.map(({value, display}) => <Option key={value} value={value}>{display}</Option>)}
              </Select>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label="Net Cost"
          >
            {getFieldDecorator('netCost', {
              initialValue: bulkConsignment.netCost,
              rules: [{
                required: true,
                message: 'Net cost is required'
              }, {
                type: 'number',
                min: 0,
                message: 'Must be at least zero'
              }]
            })(
              <InputNumber precision={2}/>
            )}
          </FormItem>
          <FormItem
            {...formItemLayout}
            label='Unit Cost Change'
          >
            {notApplicable ? 'N/A' : (<React.Fragment>
              <p key='heading'>{unitCostFactor > 1 ? 'In' : 'De'}crease by a factor
                of <b>{roundAmount(unitCostFactor, MaxDecimals)}</b></p>
              <PacksAdvice factor={unitCostFactor} packs={bulkConsignment.packs}/>
            </React.Fragment>)}
          </FormItem>
          <FormItem>
            <ButtonGroup>
              <Button
                type="primary"
                htmlType="submit"
                disabled={loading || hasErrors(getFieldsError())}
              >
                Update Received Bulk Product
              </Button>
              <Button
                htmlType="button"
                onClick={this.goBack}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </FormItem>
        </Form>
      </Card>
    );
  }
}

const UpdateReceivedBulkProductPage = Form.create()(UpdateReceivedBulkProductForm);

const mapStateToProps = state => ({
  loading: state.bulkConsignment.updatingReceivedBulkProduct || state.bulkConsignment.bulkConsignmentLoading,
  bulkConsignment: state.bulkConsignment.bulkConsignment || {quantity: 0, receivedUnit: 'g'},
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  updateReceivedBulkProduct,
  getBulkConsignment,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(UpdateReceivedBulkProductPage);



// WEBPACK FOOTER //
// ./src/BulkConsignment/UpdateReceivedBulkProductPage.js